import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion } from 'framer-motion';
import { Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TournamentBracketPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMatch, setEditMatch] = useState(null);
  const [score1, setScore1] = useState('');
  const [score2, setScore2] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchTournament();
  }, [token, id]);

  const fetchTournament = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tournaments/${id}`, { headers });
      if (res.ok) setTournament(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const submitResult = async () => {
    if (!editMatch || score1 === '' || score2 === '') return;
    const s1 = parseInt(score1), s2 = parseInt(score2);
    const winner = s1 > s2 ? editMatch.match.team1 : s2 > s1 ? editMatch.match.team2 : editMatch.match.team1;
    try {
      await fetch(`${BACKEND_URL}/api/tournaments/${id}/result`, {
        method: 'PUT', headers,
        body: JSON.stringify({ round_index: editMatch.ri, match_index: editMatch.mi, score1: s1, score2: s2, winner })
      });
      setEditMatch(null); setScore1(''); setScore2('');
      fetchTournament();
    } catch (e) { console.error(e); }
  };

  const roundNames = (total) => {
    if (total === 1) return [t('final', language)];
    if (total === 2) return [t('semiFinal', language), t('final', language)];
    if (total === 3) return [t('quarterFinal', language), t('semiFinal', language), t('final', language)];
    return Array.from({ length: total }, (_, i) => i === total - 1 ? t('final', language) : i === total - 2 ? t('semiFinal', language) : `${t('round', language)} ${i + 1}`);
  };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <h1 className="dashboard-header-title">{tournament?.name || t('tournaments', language)}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        {loading ? (
          <div className="lb-loading"><div className="lb-spinner" /></div>
        ) : !tournament ? (
          <div className="lb-empty">Tournament not found</div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Champion banner */}
            {tournament.status === 'completed' && tournament.champion && (
              <div className="tournament-champion-banner">
                <Crown size={28} />
                <span>{t('champion', language)}: <strong>{tournament.champion}</strong></span>
              </div>
            )}

            {/* Bracket */}
            <div className="bracket-container">
              {tournament.rounds.map((round, ri) => {
                const names = roundNames(tournament.rounds.length);
                return (
                  <div key={ri} className="bracket-round">
                    <div className="bracket-round-title">{names[ri]}</div>
                    <div className="bracket-matches">
                      {round.map((match, mi) => {
                        const isBye = match.team1 === 'BYE' || match.team2 === 'BYE';
                        const isTBD = match.team1 === 'TBD' || match.team2 === 'TBD';
                        const canEdit = !isBye && !isTBD && !match.winner;
                        const hasResult = match.winner && match.winner !== 'TBD' && match.winner !== 'BYE';
                        return (
                          <div key={mi} className={`bracket-match ${hasResult ? 'bracket-match-done' : ''}`}
                            onClick={() => canEdit && setEditMatch({ ri, mi, match })}>
                            <div className={`bracket-team ${match.winner === match.team1 ? 'bracket-team-winner' : ''}`}>
                              <span className="bracket-team-name">{match.team1 === 'TBD' ? '...' : match.team1 === 'BYE' ? '-' : match.team1}</span>
                              {hasResult && <span className="bracket-team-score">{match.score1}</span>}
                            </div>
                            <div className={`bracket-team ${match.winner === match.team2 ? 'bracket-team-winner' : ''}`}>
                              <span className="bracket-team-name">{match.team2 === 'TBD' ? '...' : match.team2 === 'BYE' ? '-' : match.team2}</span>
                              {hasResult && <span className="bracket-team-score">{match.score2}</span>}
                            </div>
                            {canEdit && <div className="bracket-edit-hint">{t('tapToRecord', language)}</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Edit Match Modal */}
            {editMatch && (
              <div className="upgrade-overlay" onClick={() => setEditMatch(null)}>
                <div className="upgrade-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '360px' }}>
                  <h3 className="upgrade-title">{t('recordResult', language)}</h3>
                  <div className="bracket-result-form">
                    <div className="bracket-result-team">
                      <span>{editMatch.match.team1}</span>
                      <input type="number" min="0" value={score1} onChange={e => setScore1(e.target.value)} className="bracket-score-input" placeholder="0" />
                    </div>
                    <span className="bracket-result-vs">VS</span>
                    <div className="bracket-result-team">
                      <span>{editMatch.match.team2}</span>
                      <input type="number" min="0" value={score2} onChange={e => setScore2(e.target.value)} className="bracket-score-input" placeholder="0" />
                    </div>
                  </div>
                  <button className="auth-submit" onClick={submitResult} style={{ marginTop: '16px' }}>{t('saveResult', language)}</button>
                  <button className="upgrade-btn-secondary" onClick={() => setEditMatch(null)}>{t('cancel', language)}</button>
                </div>
              </div>
            )}

            <button className="setup-back-btn" onClick={() => navigate('/tournaments')} style={{ marginTop: '20px' }}>&larr; {t('backToTournaments', language)}</button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
